import { isEmpty } from 'lodash'
import { Fragment, ReactElement } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

import { ArrowRightIcon } from '@10xLabs/react-web/components/Icons/ArrowRight'
import { CopyToClipboard, CopyToClipboardWrapper } from '@10xLabs/react-web/components/CopyToClipboard'
import { Dropdown } from 'components/v2/Dropdown'
import { FormattedDate } from '@10xLabs/react-web/components/FormattedDate'
import { FormattedRoute } from '@10xLabs/react-web/components/FormattedRoute'
import { FormattedTimeByDate } from '@10xLabs/react-web/components/FormattedTimeByDate'
import List from 'components/v2/List'
import { StyledLink } from 'components/v2/Link'
import { Table, Thead, Th, Tbody, Tr, Td } from '@10xLabs/react-web/components/Table'
import { ChangeBookings } from 'features/Changes/components/Bookings'
import { GTChangeType } from 'gql/types.generated'
import {
  ICellContainerProps,
  IDifferenceDetailsContainerProps,
  IChangeDetailsProps,
  IExtraDetailsProps,
  IDifferenceDetailsProps,
  INewChangeStop,
  IOldChangeStop
} from './types'

const ItineraryTable = styled.table`
  border-collapse: collapse;
`

const ItinearyTd = styled.td`
  font-size: 14px;
  padding: 10px 5px;
  border-bottom: 1px solid #9595b9;
  white-space: nowrap;

  &:first-child {
    padding: 10px 5px 10px 20px;
  }

  &:last-child {
    padding: 10px 20px 10px 5px;
  }
`

const EmptyTd = styled.td<TSFixMe>`
  border-bottom: 1px solid #9595b9;
  min-width: 8ch;
`

const VehicleTd = styled.td<TSFixMe>`
  width: 100%;
  padding: 10px 20px;
  white-space: nowrap;
  border-bottom: 1px solid #9595b9;
`

const CellContainer = styled.div<ICellContainerProps>`
  display: inline-block;
  margin: ${({ isAdded, isRemoved }): string => (isAdded || isRemoved ? '-4px 0' : '')};
  padding: ${({ isAdded, isRemoved }): string => (isAdded || isRemoved ? '3px 7px' : '')};
  border-width: ${({ isAdded, isRemoved }): string => (isAdded || isRemoved ? '1px' : '')};
  border-style: ${({ isAdded, isRemoved }): string => (isAdded || isRemoved ? 'solid' : '')};
  border-color: ${({ isAdded }): string => (isAdded ? '#79c0ff' : '')};
  border-color: ${({ isRemoved }): string => (isRemoved ? '#ff7979' : '')};
  border-radius: ${({ isAdded, isRemoved }): string => (isAdded || isRemoved ? '3px' : '')};
`

const Row = styled.tr<TSFixMe>`
  height: 40px;
`
const FillTd = styled.td<TSFixMe>`
  width: 100%;
  border-bottom: 1px solid #9595b9;
`

const DifferenceDetails = ({ itinerary, vehicle }: IDifferenceDetailsProps): ReactElement => {
  const intl = useIntl()

  return (
    <ItineraryTable>
      <tbody>
        <Row>
          {itinerary.map((stop, index: number): TSFixMe => {
            return (
              <Fragment key={index}>
                <ItinearyTd colSpan={2}>
                  <CellContainer
                    isAdded={stop.changes.origin === 'added'}
                    isRemoved={stop.changes.origin === 'removed'}
                  >
                    {stop.origin.name}
                  </CellContainer>
                </ItinearyTd>
                {index !== itinerary.length - 1 && (
                  <ItinearyTd>
                    <ArrowRightIcon />
                  </ItinearyTd>
                )}
              </Fragment>
            )
          })}
          <FillTd />
        </Row>
        <Row>
          {itinerary.map((stop, index): TSFixMe => {
            if (index === itinerary.length - 1) return null

            return (
              <Fragment key={index}>
                <EmptyTd />
                {stop.departureAt ? (
                  <ItinearyTd>
                    <CellContainer
                      isAdded={stop.changes.departureAt === 'added'}
                      isRemoved={stop.changes.departureAt === 'removed'}
                    >
                      <FormattedTimeByDate
                        intl={intl}
                        date={stop.departureAt}
                        timeZone={stop.origin.timezone || undefined}
                      />
                    </CellContainer>
                  </ItinearyTd>
                ) : (
                  <EmptyTd />
                )}
                {index !== itinerary.length - 1 && (
                  <ItinearyTd>
                    <ArrowRightIcon />
                  </ItinearyTd>
                )}
              </Fragment>
            )
          })}
          <FillTd colSpan={3} />
        </Row>
        <Row>
          {itinerary.map(
            (stop, index: number): TSFixMe => (
              <Fragment key={index}>
                {stop.arrivalAt ? (
                  <ItinearyTd>
                    <CellContainer
                      isAdded={stop.changes.arrivalAt === 'added'}
                      isRemoved={stop.changes.arrivalAt === 'removed'}
                    >
                      <FormattedTimeByDate
                        intl={intl}
                        date={stop.arrivalAt}
                        timeZone={stop.origin.timezone || undefined}
                      />
                    </CellContainer>
                  </ItinearyTd>
                ) : (
                  <EmptyTd />
                )}
                <EmptyTd />
                {index !== itinerary.length - 1 && (
                  <ItinearyTd>
                    <ArrowRightIcon />
                  </ItinearyTd>
                )}
              </Fragment>
            )
          )}
          <FillTd colSpan={3} />
        </Row>
        {vehicle && vehicle.id && (
          <Row>
            <VehicleTd colSpan={3 * itinerary.length}>
              <span>
                {vehicle.name}
                {vehicle.partner?.externalID ? ` (${vehicle.partner.externalID})` : ''}
              </span>
              &nbsp;
              <FormattedMessage
                id="changes.details.vehicle.capacity"
                defaultMessage="- {capacity} seats"
                values={{
                  capacity: vehicle.capacity
                }}
              />
            </VehicleTd>
          </Row>
        )}
      </tbody>
    </ItineraryTable>
  )
}

const ExtraTable = styled.table<TSFixMe>`
  border-collapse: collapse;
`

const ExtraTd = styled.td<TSFixMe>`
  padding: 8px 10px;
  text-align: left;

  :first-child {
    text-align: right;
    padding: 8px 10px 8px 0;
  }

  :last-child {
    padding: 8px 0px 8px 10px;
  }
`

const ExtraLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
`

const StopsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: -5px;
`

const StopWrapper = styled.div`
  margin: 5px;
`

const ChangedStop = styled.div`
  display: inline-block;
  margin: -4px 0;
  padding: 3px 7px;
  border-width: 1px;
  border-style: solid;
  border-radius: 3px;
`

const RemovedStop = styled(ChangedStop)`
  border-color: #ff7979;
  background-color: #eaeaea;
`

const AddedStop = styled(ChangedStop)`
  border-color: #79c0ff;
  background-color: #eef6ff;
`

const SegmentsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: -5px -20px;
`

const SegmentWrapper = styled.div`
  margin: 5px 20px;
`

const TripInfo = styled.div`
  display: flex;
`

const TripDate = styled.div`
  margin-left: 40px;
`

const TripWarningWrapper = styled.div`
  margin-left: 40px;
`

const MovedBookingsWrapper = styled.div`
  display: flex;
  gap: 20px 40px;
`

const MovedBookingsItem = styled.div`
  display: flex;
  gap: 20px;
`

const ExtraDetails = ({
  oldItinerary,
  newItinerary,
  oldSegments,
  newSegments,
  affectedTrips,
  affectedBookings,
  movedBookings,
  showBookings,
  isTripCancelledChange
}: IExtraDetailsProps): ReactElement => {
  const intl = useIntl()

  const removedStops = oldItinerary.filter((stop): TSFixMe => {
    return stop.changes.origin === 'removed'
  })
  const addedStops = newItinerary.filter((stop): TSFixMe => {
    return stop.changes.origin === 'added'
  })

  const removedSegments = oldSegments.filter((segment): TSFixMe => segment.isRemoved)
  const addedSegments = newSegments.filter((segment): TSFixMe => segment.isAdded)

  const notMovedBookings = affectedBookings.filter((booking) => {
    return !movedBookings.some(({ id }) => id === booking.id)
  })

  return (
    <ExtraTable>
      <tbody>
        <tr>
          <ExtraTd>
            <ExtraLabel>
              <FormattedMessage id="changes.details.stops.removed" defaultMessage="Stops removed:" />
            </ExtraLabel>
          </ExtraTd>
          <ExtraTd>
            {!isEmpty(removedStops) ? (
              <StopsWrapper>
                {removedStops.map(
                  (stop): TSFixMe => (
                    <StopWrapper key={stop.origin.id}>
                      <RemovedStop>{stop.origin.name}</RemovedStop>
                    </StopWrapper>
                  )
                )}
              </StopsWrapper>
            ) : (
              'N/A'
            )}
          </ExtraTd>
        </tr>
        <tr>
          <ExtraTd>
            <ExtraLabel>
              <FormattedMessage id="changes.details.stops.added" defaultMessage="Stops added:" />
            </ExtraLabel>
          </ExtraTd>
          <ExtraTd colSpan={2}>
            {!isEmpty(addedStops) ? (
              <StopsWrapper>
                {addedStops.map(
                  (stop): TSFixMe => (
                    <StopWrapper key={stop.origin.id}>
                      <AddedStop>{stop.origin.name}</AddedStop>
                    </StopWrapper>
                  )
                )}
              </StopsWrapper>
            ) : (
              'N/A'
            )}
          </ExtraTd>
        </tr>
        <tr>
          <ExtraTd>
            <ExtraLabel>
              <FormattedMessage id="changes.details.segmets.removed" defaultMessage="Segments removed:" />
            </ExtraLabel>
          </ExtraTd>
          <ExtraTd>
            {!isEmpty(removedSegments) ? (
              <Dropdown toggle={<StyledLink>{removedSegments.length}</StyledLink>} noPadding>
                <List.Container>
                  {removedSegments.map((segment): TSFixMe => {
                    return (
                      <List.Item key={segment.id}>
                        <FormattedRoute origin={segment.origin} destination={segment.destination} />
                      </List.Item>
                    )
                  })}
                </List.Container>
              </Dropdown>
            ) : (
              removedSegments.length
            )}
          </ExtraTd>
          <ExtraTd>
            {!isEmpty(removedSegments) ? (
              <SegmentsWrapper>
                {removedSegments.slice(0, 3).map(
                  (segment): TSFixMe => (
                    <SegmentWrapper key={segment.id}>
                      <FormattedRoute origin={segment.origin} destination={segment.destination} />
                    </SegmentWrapper>
                  )
                )}
              </SegmentsWrapper>
            ) : (
              'N/A'
            )}
          </ExtraTd>
        </tr>
        <tr>
          <ExtraTd>
            <ExtraLabel>
              <FormattedMessage id="changes.details.segmets.added" defaultMessage="Segments added:" />
            </ExtraLabel>
          </ExtraTd>
          <ExtraTd>
            {!isEmpty(addedSegments) ? (
              <Dropdown toggle={<StyledLink>{addedSegments.length}</StyledLink>} noPadding>
                <List.Container>
                  {addedSegments.map((segment): TSFixMe => {
                    return (
                      <List.Item key={segment.id}>
                        <FormattedRoute origin={segment.origin} destination={segment.destination} />
                      </List.Item>
                    )
                  })}
                </List.Container>
              </Dropdown>
            ) : (
              addedSegments.length
            )}
          </ExtraTd>
          <ExtraTd>
            {!isEmpty(addedSegments) ? (
              <SegmentsWrapper>
                {addedSegments.slice(0, 3).map(
                  (segment): TSFixMe => (
                    <SegmentWrapper key={segment.id}>
                      <FormattedRoute origin={segment.origin} destination={segment.destination} />
                    </SegmentWrapper>
                  )
                )}
              </SegmentsWrapper>
            ) : (
              'N/A'
            )}
          </ExtraTd>
        </tr>
        {showBookings && (
          <>
            <tr>
              <ExtraTd>
                <ExtraLabel>
                  <FormattedMessage id="changes.details.trips.affected" defaultMessage="Trips modified:" />
                </ExtraLabel>
              </ExtraTd>
              <ExtraTd>{affectedTrips.length}</ExtraTd>
              {affectedTrips.length === 1 && (
                <ExtraTd>
                  <TripInfo>
                    <CopyToClipboardWrapper>
                      <div>{affectedTrips[0].id.substring(0, 8)}</div>
                      <CopyToClipboard str={affectedTrips[0].id} />
                    </CopyToClipboardWrapper>
                    <TripDate>
                      <FormattedMessage id="changes.details.trip.departureDate" defaultMessage="Departure:" />
                      &nbsp;
                      <FormattedDate
                        intl={intl}
                        value={affectedTrips[0].departureAt}
                        month="2-digit"
                        day="2-digit"
                        year="numeric"
                        timeZone={newItinerary[0].origin.timezone || undefined}
                      />
                    </TripDate>
                    {isTripCancelledChange && (
                      <TripWarningWrapper>
                        <WarningMessage>
                          <FormattedMessage id="dictionary.cancelled" defaultMessage="Cancelled" />
                        </WarningMessage>
                      </TripWarningWrapper>
                    )}
                  </TripInfo>
                </ExtraTd>
              )}
            </tr>
            <tr>
              <ExtraTd>
                <ExtraLabel>
                  <FormattedMessage id="changes.details.bookings.affected" defaultMessage="Bookings affected:" />
                </ExtraLabel>
              </ExtraTd>
              <ExtraTd>
                <ChangeBookings bookings={affectedBookings} />
              </ExtraTd>
              <ExtraTd>
                <MovedBookingsWrapper>
                  <MovedBookingsItem>
                    <ExtraLabel>
                      <FormattedMessage id="changes.details.bookings.moved" defaultMessage="Moved Bookings:" />
                    </ExtraLabel>
                    <ChangeBookings bookings={movedBookings} />
                  </MovedBookingsItem>
                  <MovedBookingsItem>
                    <ExtraLabel>
                      <FormattedMessage id="changes.details.bookings.notMoved" defaultMessage="Not moved Bookings:" />
                    </ExtraLabel>
                    <ChangeBookings bookings={notMovedBookings} />
                  </MovedBookingsItem>
                </MovedBookingsWrapper>
              </ExtraTd>
            </tr>
          </>
        )}
      </tbody>
    </ExtraTable>
  )
}

const StyledTh = styled(Th)`
  padding-left: 20px;
`

const ServiceTr = styled(Tr)`
  border-top: 3px solid #9595b9;
`

const RouteServiceTd = styled(Td)`
  padding: 10px;
  border-right: 1px solid #9595b9;
`

const RouteServiceCode = styled.div`
  padding: 15px;
  font-size: 14px;
  font-weight: 500;
  background-color: #eef6ff;
  border: 1px solid #79c0ff;
  border-radius: 3px;
`

const DifferenceDetailsTd = styled(Td)`
  padding: 0;
`

const DifferenceDetailsContainer = styled.div<IDifferenceDetailsContainerProps>`
  background-color: ${({ isNew }): string => (isNew ? '#eef6ff' : '#eaeaea')};
  border-left: ${({ isNew }): string => (isNew ? '1px solid #9595b9' : '')};
`

const DetailsTd = styled(Td)`
  :first-child {
    padding-left: 20px;
  }

  :last-child {
    padding-right: 20px;
  }
`

const DetailsTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 5px;
`

const WarningMessageWrapper = styled.div`
  margin-top: 20px;
`

const WarningMessage = styled.span`
  font-weight: 500;
  color: #f71b03;
`
export const ChangeDetails = ({ change, showBookings }: IChangeDetailsProps): ReactElement => {
  const isTripCancelledChange = change.type === GTChangeType.TripCancelled

  return (
    <Table>
      <Thead>
        <Tr>
          <Th>
            <FormattedMessage id="changes.details.service.label" defaultMessage="Service" />
          </Th>
          <StyledTh>
            <FormattedMessage id="changes.details.before.label" defaultMessage="Before" />
          </StyledTh>
          <StyledTh>
            <FormattedMessage id="changes.details.after.label" defaultMessage="After" />
          </StyledTh>
        </Tr>
      </Thead>
      <Tbody>
        {change.difference.map((difference, index: number): TSFixMe => {
          const { itinerary, segments, vehicle } = difference

          if (isEmpty(itinerary) || isEmpty(segments)) {
            return 'To Implement'
          }

          const oldItinerary: IOldChangeStop[] = itinerary.old.map((stop): TSFixMe => {
            const newStop = itinerary.new.find(({ origin }): TSFixMe => origin.id === stop.origin.id)
            const isArrivalDateModified = isEmpty(newStop) || stop.arrivalAt !== newStop?.arrivalAt
            const isDeparturelDateModified = isEmpty(newStop) || stop.departureAt !== newStop?.departureAt
            return {
              ...stop,
              changes: {
                arrivalAt: isArrivalDateModified ? 'removed' : null,
                departureAt: isDeparturelDateModified ? 'removed' : null,
                origin: isEmpty(newStop) ? 'removed' : null
              }
            }
          })
          const newItinerary: INewChangeStop[] = itinerary.new.map((stop): TSFixMe => {
            const oldStop = itinerary.old.find(({ origin }): TSFixMe => origin.id === stop.origin.id)
            const isArrivalDateModified = isEmpty(oldStop) || stop.arrivalAt !== oldStop?.arrivalAt
            const isDeparturelDateModified = isEmpty(oldStop) || stop.departureAt !== oldStop?.departureAt

            return {
              ...stop,
              changes: {
                arrivalAt: isArrivalDateModified ? 'added' : null,
                departureAt: isDeparturelDateModified ? 'added' : null,
                origin: isEmpty(oldStop) ? 'added' : null
              }
            }
          })
          const oldSegments = segments.old.map((segment): TSFixMe => {
            return {
              ...segment,
              isRemoved: isEmpty(segments.new.find(({ id }): TSFixMe => id === segment.id))
            }
          })
          const newSegments = segments.new.map((segment): TSFixMe => {
            return {
              ...segment,
              isAdded: isEmpty(segments.old.find(({ id }): TSFixMe => id === segment.id))
            }
          })

          return (
            <Fragment key={index}>
              <ServiceTr>
                <RouteServiceTd rowSpan={2}>
                  <RouteServiceCode>{difference.routeService.code}</RouteServiceCode>
                  {isTripCancelledChange && (
                    <WarningMessageWrapper>
                      <WarningMessage>
                        <FormattedMessage id="dictionary.cancelled" defaultMessage="Cancelled" />
                      </WarningMessage>
                    </WarningMessageWrapper>
                  )}
                </RouteServiceTd>
                <DifferenceDetailsTd>
                  <DifferenceDetailsContainer>
                    <DifferenceDetails itinerary={oldItinerary} vehicle={vehicle && vehicle.old} />
                  </DifferenceDetailsContainer>
                </DifferenceDetailsTd>
                <DifferenceDetailsTd>
                  <DifferenceDetailsContainer isNew>
                    <DifferenceDetails itinerary={newItinerary} vehicle={vehicle && vehicle.new} />
                  </DifferenceDetailsContainer>
                </DifferenceDetailsTd>
              </ServiceTr>
              <Tr>
                <DetailsTd colSpan={2}>
                  <DetailsTitle>
                    <FormattedMessage id="changes.details.title" defaultMessage="Details" />
                  </DetailsTitle>
                  <ExtraDetails
                    oldItinerary={oldItinerary}
                    newItinerary={newItinerary}
                    oldSegments={oldSegments}
                    newSegments={newSegments}
                    affectedTrips={change.trips}
                    affectedBookings={change.bookings}
                    movedBookings={change.movedBookings}
                    showBookings={showBookings}
                    isTripCancelledChange={isTripCancelledChange}
                  />
                </DetailsTd>
              </Tr>
            </Fragment>
          )
        })}
      </Tbody>
    </Table>
  )
}
